import { env } from './env';

import {
  initializeApp,
  getApp,
  getApps,
  FirebaseOptions,
  FirebaseError,
} from 'firebase/app';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  getAuth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPopup,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  connectAuthEmulator,
  User as FirebaseUser,
  validatePassword as firebaseValidatePassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { initializeAppCheck, AppCheck } from 'firebase/app-check';
import { appCheckCustomProvider } from './app-check';

import { AuthError } from './errors';

const firebaseConfig: FirebaseOptions = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

export const firebaseApp =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

export const firebaseAuth = getAuth(firebaseApp);
// Connect to Auth Emulator in development
// IMPORTANT: This must be called before any other auth operations
if (env.VITE_AUTH_EMULATOR_HOST) {
  connectAuthEmulator(firebaseAuth, env.VITE_AUTH_EMULATOR_HOST);
}

export const firebaseEmailPasswordAuth = new EmailAuthProvider();
export const firebaseGoogleAuth = new GoogleAuthProvider();

const firebaseErrorMessages: Record<string, string> = {
  // Generic login failure (do NOT reveal which field is wrong)
  'auth/invalid-credential': 'The email or password you entered is incorrect.',
  'auth/wrong-password': 'The email or password you entered is incorrect.',
  'auth/user-not-found': 'The email or password you entered is incorrect.',
  'auth/invalid-email': 'The email or password you entered is incorrect.',

  // Account / security
  'auth/user-disabled':
    'Your account has been deactivated. Please contact your organization administrator for help.',
  'auth/email-not-verified':
    'Please verify your email before signing in. Check your inbox for the verification link.',

  // Rate limiting / infra
  'auth/too-many-requests':
    'Too many login attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed':
    'Network error occurred. Please check your internet connection and try again.',
  'auth/internal-error': 'Unexpected issue encountered. Please try again later',
};

export const signIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      firebaseAuth,
      email,
      password,
    );

    const isPasswordUser = userCredential.user.providerData.some(
      (provider) => provider.providerId === 'password',
    );

    if (isPasswordUser && !userCredential.user.emailVerified) {
      await firebaseAuth.signOut();
      throw new AuthError(
        firebaseErrorMessages['auth/email-not-verified'],
        'auth/email-not-verified',
      );
    }

    return userCredential.user;
  } catch (error: unknown) {
    // Re-throw already normalized errors
    if (error instanceof AuthError) {
      throw error;
    }

    // Normalize Firebase errors
    if (error instanceof FirebaseError) {
      const message =
        firebaseErrorMessages[error.code] ??
        firebaseErrorMessages['auth/invalid-credential'];

      throw new AuthError(message, error.code);
    }

    // Fallback
    throw new AuthError(
      'An unexpected error occurred. Please try again.',
      'auth/unknown-error',
    );
  }
};

/**
 * Exchanges a server-minted custom token for a Firebase session. Used by the Driver ID
 * channel, where the driver has no email and Firebase therefore cannot verify their
 * password — our server does that and vouches for them with this token.
 */
export const signInWithToken = async (token: string) => {
  try {
    const userCredential = await signInWithCustomToken(firebaseAuth, token);
    return userCredential.user;
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      throw new AuthError(
        firebaseErrorMessages[error.code] ??
          'Could not sign you in. Please try again.',
        error.code,
      );
    }
    throw new AuthError(
      'An unexpected error occurred. Please try again.',
      'auth/unknown-error',
    );
  }
};

export const signInWithGoogle = async () => {
  if (!env.VITE_ENABLE_SIGN_UP_WITH_GOOGLE) {
    throw new Error('Google authentication is not enabled');
  }

  const userCredential = await signInWithPopup(
    firebaseAuth,
    firebaseGoogleAuth,
  );
  return userCredential.user;
};

export const signUp = async (name: string, email: string, password: string) => {
  const userCredential = await createUserWithEmailAndPassword(
    firebaseAuth,
    email,
    password,
  );

  await updateProfile(userCredential.user, {
    displayName: name,
  });

  await sendEmailVerification(userCredential.user);

  await firebaseAuth.signOut();

  return userCredential.user;
};

export const signOut = async () => {
  await firebaseAuth.signOut();
};

export const onAuthStateChange = (
  callback: (user: FirebaseUser | null) => void,
) => {
  return onAuthStateChanged(firebaseAuth, callback);
};

export const validatePassword = async (password: string) => {
  if (env.VITE_AUTH_EMULATOR_HOST) {
    // Password validation is not supported in the Auth Emulator
    // "identitytoolkit.getPasswordPolicy is not implemented in the Auth Emulator."
    // Return a mock valid result
    return {
      isValid: true,
      meetsMinPasswordLength: true,
      meetsMaxPasswordLength: true,
      meetsLowercaseRequirement: true,
      meetsUppercaseRequirement: true,
      meetsNumericRequirement: true,
      meetsNonAlphanumericRequirement: true,
      passwordPolicy: null,
    };
  }

  return firebaseValidatePassword(firebaseAuth, password);
};

export const getAuthToken = async () => {
  const user = await firebaseAuth.currentUser;
  if (!user) {
    return;
  }
  return await user.getIdToken();
};

export const resetPassword = async (email: string) => {
  await sendPasswordResetEmail(firebaseAuth, email);
};

// Initialize App Check with Turnstile provider (only in production with site key)
let appCheck: AppCheck | null = null;
if (
  (env.VITE_NODE_ENV === 'production' || env.VITE_NODE_ENV === 'staging') &&
  env.VITE_TURNSTILE_SITE_KEY
) {
  appCheck = initializeAppCheck(firebaseApp, {
    provider: appCheckCustomProvider,
    isTokenAutoRefreshEnabled: true,
  });
}
export const appCheckInstance = appCheck;
